import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { SingleOtherFilters, type SingleOtherFiltersProps } from '~/components/Filters/OtherFilters/SingleOtherFilters';
import { FilterTrigger } from '~/components/Filters/SingleFilters/FilterTrigger';
import { baseFiltersOptions } from '~/components/FiltersMenu/types';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { removeFilterAction } from '~/store/filters/actions';
import { type SelectedFilter } from '~/store/filters/types';

export type OtherFiltersTriggerProps = SingleOtherFiltersProps;

export const OtherFiltersTrigger = memo(
  ({ onApplyClick, otherFilters, onRemoveClick, onOpenChange }: OtherFiltersTriggerProps) => {
    const dispatch = useDispatch();
    const { bookmarkedParam, isOnBoardsParam, hasVersionsParam, hasOpenCommentsParam, untaggedParam } =
      useFilterParams();

    const content = useMemo(
      () => <SingleOtherFilters otherFilters={otherFilters} onApplyClick={onApplyClick} />,
      [onApplyClick, otherFilters],
    );

    const appliedCount = useMemo(() => {
      let count = 0;
      if (bookmarkedParam?.is) {
        count += 1;
      }

      if (isOnBoardsParam?.is) {
        count += 1;
      }

      if (hasVersionsParam?.is) {
        count += 1;
      }

      if (hasOpenCommentsParam?.is) {
        count += 1;
      }

      if (untaggedParam?.is) {
        count += 1;
      }

      return count;
    }, [bookmarkedParam?.is, hasOpenCommentsParam?.is, hasVersionsParam?.is, isOnBoardsParam?.is, untaggedParam?.is]);

    const removeFilter = useCallback(async () => {
      const filter: SelectedFilter = { type: 'other' };
      dispatch(removeFilterAction({ filter }));
      onRemoveClick?.([filter]);
    }, [dispatch, onRemoveClick]);

    return (
      <FilterTrigger
        onRemoveClick={removeFilter}
        count={appliedCount}
        filterContent={content}
        title={baseFiltersOptions['other'].label}
        Icon={baseFiltersOptions['other'].icon}
        onOpenChange={onOpenChange}
      />
    );
  },
);

OtherFiltersTrigger.displayName = 'OtherFiltersTrigger';
